import { useState, useEffect, useRef } from 'react'
import { Search, X, Users } from 'lucide-react'
import { toast } from 'sonner'
import { getUsers, type User } from '@/api/users'

function ini(name: string) {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
}

interface Props {
  onSelect: (user: User) => void
  onClear?: () => void
  value?: User | null
  placeholder?: string
  disabled?: boolean
  inputClassName?: string
}

export function MemberSearch({ onSelect, onClear, value, placeholder = 'Buscar por nombre o número...', disabled, inputClassName }: Props) {
  const [query, setQuery]       = useState('')
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [open, setOpen]         = useState(false)
  const [cursor, setCursor]     = useState(-1)
  const ref      = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    getUsers().then(r => setAllUsers(r.users)).catch(() => toast.error('Error al cargar miembros'))
  }, [])

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const filtered = query.length >= 1
    ? allUsers.filter(u =>
        u.name.toLowerCase().includes(query.toLowerCase()) ||
        u.employee_no.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 7)
    : []

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, filtered.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)) }
    if (e.key === 'Enter' && filtered[cursor]) select(filtered[cursor])
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur() }
  }

  function select(u: User) {
    onSelect(u)
    setOpen(false)
    setQuery('')
    setCursor(-1)
  }

  /* ── Estado: miembro ya seleccionado ── */
  if (value) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-accent/8 border border-accent/30">
        <div className="w-10 h-10 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center shrink-0 font-black text-sm text-accent select-none">
          {ini(value.name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-text-primary text-sm font-bold truncate leading-tight">{value.name}</p>
          <p className="text-text-muted text-[10px] font-mono mt-0.5">CC {value.employee_no}</p>
        </div>
        <button
          onClick={onClear}
          aria-label="Quitar selección"
          className="p-1.5 rounded-lg text-text-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors cursor-pointer shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    )
  }

  /* ── Estado: búsqueda ── */
  return (
    <div className="relative" ref={ref}>
      {/* Input */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
        <input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); setCursor(-1) }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={inputClassName ?? "w-full bg-background border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50 transition-all disabled:opacity-50"}
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setCursor(-1); inputRef.current?.focus() }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && query.length >= 1 && (
        <div className="absolute z-50 top-full mt-1.5 w-full bg-surface border border-border rounded-xl shadow-2xl shadow-black/20 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-7 text-text-muted">
              <Users className="w-6 h-6 opacity-40" />
              <p className="text-xs">Sin resultados para <span className="font-semibold text-text-primary">"{query}"</span></p>
            </div>
          ) : (
            <>
              <p className="px-3.5 pt-2.5 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-text-muted border-b border-border/60">
                {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
              </p>
              {filtered.map((u, i) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => select(u)}
                  onMouseEnter={() => setCursor(i)}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 transition-colors text-left ${
                    cursor === i ? 'bg-accent/8' : 'hover:bg-background/70'
                  }`}
                >
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-accent/15 border border-accent/20 flex items-center justify-center shrink-0 font-black text-[11px] text-accent select-none">
                    {ini(u.name)}
                  </div>

                  {/* Nombre + número */}
                  <div className="flex-1 min-w-0">
                    <p className="text-text-primary text-sm font-semibold truncate leading-tight">{u.name}</p>
                    <p className="text-text-muted text-[10px] font-mono mt-0.5">CC {u.employee_no}</p>
                  </div>

                  {/* Indicadores de acceso */}
                  <div className="flex items-center gap-1 shrink-0">
                    {u.face_uploaded && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Cara" />}
                    {u.card_no       && <span className="w-1.5 h-1.5 rounded-full bg-blue-400"    title="Tarjeta" />}
                    {u.pin           && <span className="w-1.5 h-1.5 rounded-full bg-purple-400"  title="PIN" />}
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}
